import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import portalApi, { apiError } from './portalApi';
import { T } from './portalTheme';
import { AuthLayout, AuthHeading, Field, PasswordInput, Button, Alert } from './PortalWidgets';
import { usePortalAuth } from '../../context/PortalAuthContext';

// /questionnaire/portal/reset-password?token=<token> -- the link in the reset email. A successful
// reset signs the account straight in (holding the link proves ownership of the inbox).
export default function PortalResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const { loginWithToken } = usePortalAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const mismatch = !!confirm && password !== confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const { data } = await portalApi.post('/v1/questionnaire-responses/portal/reset-password', { token, password });
      loginWithToken(data.access_token, data.account);
      navigate('/questionnaire/portal/dashboard', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not reset your password. Request a new link and try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <AuthHeading title="Choose a new password" subtitle="You'll be signed in as soon as it's saved." />
      {!token ? (
        <Alert tone="error">
          This link is missing its reset code. <Link to="/questionnaire/portal/forgot-password" style={{ color: T.red, fontWeight: 700 }}>Request a new link</Link>.
        </Alert>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="New password" hint="At least 8 characters.">
            <PasswordInput value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" required autoFocus showStrength />
          </Field>
          <Field label="Confirm new password" error={mismatch ? "Passwords don't match." : undefined}>
            <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" required />
          </Field>
          {error && (
            <Alert tone="error">
              {error}{' '}
              {/expired|invalid|used/i.test(error) && <Link to="/questionnaire/portal/forgot-password" style={{ color: T.red, fontWeight: 700 }}>Request a new link</Link>}
            </Alert>
          )}
          <Button type="submit" size="lg" full loading={submitting}>{submitting ? 'Saving…' : 'Save password and sign in'}</Button>
        </form>
      )}
    </AuthLayout>
  );
}
